import { useEffect, useContext } from 'react';
import { useMutation } from 'react-apollo';
import validator from 'validator';
import { get, replace, join, omit } from 'lodash';
import { loader } from 'graphql.macro';

// Services
import { SIGN_IN_TYPE } from 'services/session';

// Hooks
import { SignInContext } from '../../../../hooks';

// Config
import { SERVER_ERRORS_HANDLER, ERROR_CONNECTION, MESSAGES_MAP, ERROR_PIN_REFETCH, ERROR_PIN_RETRY } from 'config';

// GraphQL
const signInQuery = loader('src/graphql/mutations/signIn.gql');

type USE_STEP_PROPS_TYPE = {
  formValues?: object,
  signIn: SIGN_IN_TYPE,
};

export const useStep = ({
  formValues,
  signIn
}: USE_STEP_PROPS_TYPE) => {

  const usedSignInContext = useContext(SignInContext);
  const {
    loading,
    values,
    attempts,
    step,
    handleUpdate,
    handleNext,
    handleFail,
    handleLoading,
    handleValues
  } = usedSignInContext;

  const [
    handleSignInQuery,
    signInQueryResult
  ] = useMutation(
    signInQuery,
    {
      fetchPolicy: 'no-cache',
      onError: (error: any) => SERVER_ERRORS_HANDLER(error, (error?: any) => handleFail(!!error))
    }
  );

  const { loading: signInQueryLoading, data: signInQueryData, error: signInQueryError } = signInQueryResult;

  useEffect(() => {
    handleLoading(!!signInQueryLoading);
  }, [signInQueryLoading]);

  useEffect(() => {
    handleUpdate({
      handleRefetch: () => {
        handleSignInQuery({
          variables: { ...omit(values, ['login', 'pin']) }
        });
      }
    });
  }, [values]);

  useEffect(() => {
    if (!signInQueryData || loading) {
      return;
    }
    const user = get(signInQueryData, 'signIn.user');
    const token = get(signInQueryData, 'signIn.token');
    const isError = get(signInQueryData, 'signIn.__typename') === 'Error';
    const message = get(signInQueryData, 'signIn.message');
    const remember = get(
      formValues,
      'remember',
      get(values, 'remember', false)
    );

    if (token) { 
      signIn({ ...user, token, ...(remember ? { expire: 0 } : {}) });
    }

    const dataError = isError && get(MESSAGES_MAP, `${message}`, message);
    const currentErrors = [
      ...(signInQueryError ? [ERROR_CONNECTION] : []),
      ...(dataError ? [dataError] : []),
      ...(!token && step === 1
        ? [attempts <= 1 ? ERROR_PIN_REFETCH : ERROR_PIN_RETRY]
        : [])
    ];

    currentErrors.length <= 0
      ? handleNext({
          error: false
        })
      : handleUpdate({
          error: join(currentErrors, '\n'),
          attempts: attempts > 0 ? attempts - 1 : 0
        });

  }, [loading, signInQueryData, signInQueryError]);

  const handleSubmit = (event: React.SyntheticEvent) => {
    get(event, 'preventDefault') && event.preventDefault();
    const login = replace(
      get(formValues, 'login', get(values, 'login')),
      / /g,
      ''
    );
    const pin = parseInt(get(formValues, 'pin', get(values, 'pin', '')), 10);
    const isPhone =
      login && validator.isMobilePhone(login, 'ru-RU', { strictMode: false });
    
    const stepValues = {
      [isPhone ? 'phone' : 'email'] : login,
      pin
    };
    handleValues({ ...stepValues, login });
    return handleSignInQuery({ variables: { ...stepValues } });
  };

  return {
    ...usedSignInContext,
    handleSubmit,
  };
};

export default useStep;
